import { z } from 'zod';

import { MEMBERSHIP_TYPES } from '../constants.js';

/** Email validation — lowercase, trimmed, RFC-loose. */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'Слишком короткий email')
  .max(254, 'Слишком длинный email')
  .email('Неверный формат email');

/**
 * Password policy for new passwords (registration / change-password).
 * - min 9 chars
 * - must contain at least one letter and one digit/symbol
 * - max 256 to prevent DOS in argon2 (also OWASP recommendation)
 */
export const newPasswordSchema = z
  .string()
  .min(9, 'Пароль должен быть не короче 9 символов')
  .max(256, 'Пароль слишком длинный')
  .refine((v) => /[a-zA-Zа-яА-Я]/.test(v) && /[^a-zA-Zа-яА-Я]/.test(v), {
    message: 'Пароль должен содержать буквы и цифры/символы',
  });

/** Login does NOT enforce policy — old accounts may have weak legacy passwords. */
export const loginPasswordSchema = z.string().min(1, 'Введите пароль').max(256);

export const loginRequestSchema = z.object({
  email: emailSchema,
  password: loginPasswordSchema,
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const meResponseSchema = z.object({
  userId: z.string().uuid(),
  email: z.string().email(),
  workspaceUserId: z.string().uuid().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  isAdmin: z.boolean(),
  /** 'employee' | 'company' — drives UI scoping (e.g. Rewards is employee-only). */
  membershipType: z.enum(MEMBERSHIP_TYPES),
  /** For company-membership users: the client they belong to. */
  companyId: z.string().uuid().nullable(),
  avatarUrl: z.string().max(512).nullable(),
  /** Workspace master switch for health bars, points, tiers, and bonus shop. */
  gamificationEnabled: z.boolean(),
  /** Per-user opt-in: show game elements on Home when gamification is enabled. */
  showGameElements: z.boolean(),
  /** Platform superadmin — cross-tenant controls (fixed SUPERADMIN_EMAIL account). */
  isSuperadmin: z.boolean(),
  /** Workspace email verified (self-registration). */
  emailVerified: z.boolean(),
  /** ISO deadline for email verification; null when verified or not applicable. */
  verificationDeadline: z.string().datetime().nullable(),
  /** Workspace blocked until email is verified (past 7-day deadline). */
  workspaceSuspended: z.boolean(),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

/** Self-service profile update (name / email / optional password change). */
export const updateProfileSchema = z
  .object({
    firstName: z.string().trim().min(1, 'Введите имя').max(120).optional(),
    lastName: z.string().trim().max(120).optional(),
    email: emailSchema.optional(),
    currentPassword: z.string().min(1).optional(),
    newPassword: newPasswordSchema.optional(),
    showGameElements: z.boolean().optional(),
  })
  .refine((d) => !d.newPassword || Boolean(d.currentPassword), {
    message: 'Введите текущий пароль для смены',
    path: ['currentPassword'],
  });
export type UpdateProfileDto = z.infer<typeof updateProfileSchema>;

/** Login returns the same shape as /me so the client can populate auth state in one call. */
export const loginResponseSchema = meResponseSchema;
export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const verifyEmailResponseSchema = z.object({
  ok: z.literal(true),
  message: z.string(),
});
export type VerifyEmailResponseDto = z.infer<typeof verifyEmailResponseSchema>;

export const resendVerificationResponseSchema = z.object({
  ok: z.literal(true),
  message: z.string(),
});
export type ResendVerificationResponseDto = z.infer<typeof resendVerificationResponseSchema>;

export const changePasswordRequestSchema = z.object({
  currentPassword: loginPasswordSchema,
  newPassword: newPasswordSchema,
});
export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>;

export const forgotPasswordRequestSchema = z.object({
  email: emailSchema,
});
export type ForgotPasswordRequest = z.infer<typeof forgotPasswordRequestSchema>;

export const forgotPasswordResponseSchema = z.object({
  ok: z.literal(true),
});
export type ForgotPasswordResponseDto = z.infer<typeof forgotPasswordResponseSchema>;

export const passwordResetCompleteRequestSchema = z.object({
  token: z.string().trim().min(1, 'Ссылка недействительна'),
  newPassword: newPasswordSchema,
});
export type PasswordResetCompleteRequest = z.infer<typeof passwordResetCompleteRequestSchema>;

export const passwordResetCompleteResponseSchema = z.object({
  ok: z.literal(true),
  message: z.string(),
});
export type PasswordResetCompleteResponseDto = z.infer<typeof passwordResetCompleteResponseSchema>;
